import React, { useState, useEffect } from 'react';
import LoginHeader from './components/LoginHeader';
import AuthTabs from './components/AuthTabs';
import LoginForm from './components/LoginForm';
import MagicLinkForm from './components/MagicLinkForm';
import AuthFooter from './components/AuthFooter';

const LoginPage = () => {
  const [activeTab, setActiveTab] = useState('password');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [magicLinkSuccess, setMagicLinkSuccess] = useState(false);

  // Mock credentials for different user roles
  const mockCredentials = {
    'admin@craneprice.com': { password: 'admin123', role: 'admin' },
    'dealer@example.com': { password: 'dealer123', role: 'dealer_pro' },
    'lender@bank.com': { password: 'lender123', role: 'lender_pro' },
    'user@example.com': { password: 'user123', role: 'user' }
  };

  useEffect(() => {
    // Clear error when switching tabs
    setError('');
    setMagicLinkSuccess(false);
  }, [activeTab]);

  const handleTabChange = (tabId) => {
    if (!isLoading) {
      setActiveTab(tabId);
    }
  };

  const handlePasswordLogin = async (formData) => {
    setIsLoading(true);
    setError('');

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));

      const { email, password } = formData;
      const mockUser = mockCredentials?.[email];

      if (!mockUser || mockUser?.password !== password) {
        throw new Error('Invalid email or password. Please check your credentials and try again.');
      }

      // Simulate successful login
      console.log('Login successful:', { email, role: mockUser?.role });
      
      // Role-based redirect
      const redirectMap = {
        admin: '/admin-panel',
        dealer_pro: '/inventory-management',
        lender_pro: '/portfolio-dashboard',
        user: '/crane-search-dashboard'
      };

      const redirectPath = redirectMap?.[mockUser?.role] || '/crane-search-dashboard';
      
      // Store user session (in real app, this would be handled by auth service)
      localStorage.setItem('user', JSON.stringify({
        email,
        role: mockUser?.role,
        loginTime: new Date()?.toISOString()
      }));

      // Redirect to appropriate dashboard
      window.location.href = redirectPath;

    } catch (err) {
      setError(err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLinkRequest = async (email) => {
    setIsLoading(true);
    setError('');

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Check if email exists in our mock system
      if (!mockCredentials?.[email]) {
        throw new Error('Email not found. Please check your email address or create a new account.');
      }

      // Simulate successful magic link send
      console.log('Magic link sent to:', email);
      setMagicLinkSuccess(true);

    } catch (err) {
      setError(err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-surface rounded-2xl shadow-card border border-border p-8">
          <LoginHeader />
          
          <AuthTabs 
            activeTab={activeTab}
            onTabChange={handleTabChange}
            isLoading={isLoading}
          />

          <div className="mb-8">
            {activeTab === 'password' ? (
              <LoginForm
                onSubmit={handlePasswordLogin}
                isLoading={isLoading}
                error={error}
              />
            ) : (
              <MagicLinkForm
                onSubmit={handleMagicLinkRequest}
                isLoading={isLoading}
                success={magicLinkSuccess}
                error={error}
              />
            )}
          </div>

          {!magicLinkSuccess && (
            <AuthFooter isLoading={isLoading} />
          )}
        </div>

        {/* Demo Credentials Info */}
        <div className="mt-6 p-4 bg-muted rounded-lg border border-border">
          <h3 className="text-sm font-medium text-text-primary mb-2">Demo Credentials:</h3>
          <div className="space-y-1 text-xs text-text-secondary">
            <div>Admin: admin@craneprice.com / admin123</div>
            <div>Dealer Pro: dealer@example.com / dealer123</div>
            <div>Lender Pro: lender@bank.com / lender123</div>
            <div>Basic User: user@example.com / user123</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;